import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Search } from "lucide-react";
import { useState } from "react";
import Seo, { publicUrl } from "@/components/Seo";

export default function PortalLanding() {
  const [q, setQ] = useState("");
  const nav = useNavigate();

  return (
    <div className="min-h-screen" style={{ background: "var(--cc-bg)" }}>
      <Seo
        title="Rabah Concept Recruitment | Care Jobs Across the UK"
        description="CQC-regulated care recruitment — Care Support Workers, Senior Carers, Registered Managers. Browse live vacancies and apply online in minutes."
        canonical={publicUrl("/")}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "Rabah Concept Recruitment",
          "url": publicUrl("/"),
          "logo": publicUrl("/rabah-logo.png"),
          "email": "recruitment@rabahconcept.co.uk",
          "telephone": "+44 03303211535",
          "sameAs": [],
        }}
      />
      {/* Nav */}
      <header className="cc-topbar px-8 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="portal-brand">
          <img src="/rabah-logo.png" alt="Rabah Concept" className="w-10 h-10 object-contain" />
          <div>
            <div className="font-display font-bold text-lg leading-none">Rabah Concept</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--cc-text-soft)] mt-1">Careers</div>
          </div>
        </Link>
        <div className="flex items-center gap-2">
          <Link to="/jobs" className="btn-ghost" data-testid="nav-jobs">Browse jobs</Link>
          <Link to="/track" className="btn-ghost" data-testid="nav-track">Track application</Link>
          <Link to="/login" className="btn-secondary" data-testid="nav-staff-login">Staff sign in</Link>
        </div>
      </header>

      {/* Hero */}
      <section className="px-8 pt-20 pb-24 max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7 fade-up">
          <div className="label-eyebrow mb-4">Careers in adult social care</div>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.02] mb-6">
            Bring care<br />
            <span style={{ color: "var(--cc-primary)" }}>where it matters</span><br />
            most.
          </h1>
          <p className="text-lg text-[var(--cc-text-soft)] max-w-xl mb-8 leading-relaxed">
            Join Rabah Concept's growing network of compassionate care professionals. Transparent process, fair pay, and clear progression — every step compliant with CQC safer recruitment.
          </p>
          <div className="flex gap-3 items-center max-w-xl">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[var(--cc-text-soft)]" />
              <input
                className="cc-input pl-11 py-3.5"
                placeholder="Search care roles, e.g. 'support worker'…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && nav(`/jobs?q=${encodeURIComponent(q)}`)}
                data-testid="hero-search-input"
              />
            </div>
            <button className="btn-primary py-3.5" onClick={() => nav(`/jobs?q=${encodeURIComponent(q)}`)} data-testid="hero-search-button">
              Find roles <ArrowRight className="w-4 h-4" />
            </button>
          </div>
          <div className="flex gap-8 mt-10 text-sm">
            <div><div className="font-dash text-3xl font-bold">200+</div><div className="text-[var(--cc-text-soft)]">Live vacancies</div></div>
            <div><div className="font-dash text-3xl font-bold">14 days</div><div className="text-[var(--cc-text-soft)]">Avg. time to offer</div></div>
            <div><div className="font-dash text-3xl font-bold">CQC ★</div><div className="text-[var(--cc-text-soft)]">Safer recruitment</div></div>
          </div>
        </div>
        <div className="lg:col-span-5 relative">
          <div className="aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl" style={{ boxShadow: "0 24px 60px -20px rgba(27,59,54,0.3)" }}>
            <img
              src="https://images.unsplash.com/photo-1765896387387-0538bc9f997e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwxfHxjYXJlZ2l2ZXIlMjBzbWlsaW5nJTIwZWxkZXJseXxlbnwwfHx8fDE3Nzg1MTE5MjV8MA&ixlib=rb-4.1.0&q=85"
              alt="Carer with elderly resident"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute -bottom-6 -left-6 cc-card p-5 max-w-xs hidden md:block">
            <div className="label-eyebrow mb-1">Today's spotlight</div>
            <div className="font-semibold">Senior Care Assistant · Manchester</div>
            <div className="text-xs text-[var(--cc-text-soft)] mt-1">£24,000 – £27,500 · Full-time</div>
          </div>
        </div>
      </section>

      {/* Why */}
      <section className="bg-[var(--cc-surface)] py-20 px-8">
        <div className="max-w-7xl mx-auto">
          <div className="label-eyebrow mb-3">Why CareConnect</div>
          <h2 className="font-display text-4xl md:text-5xl font-bold mb-12 max-w-3xl">A recruitment process built on trust, transparency and care.</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { t: "Clear timeline", d: "Track every stage from application to offer — no black boxes.", k: "01" },
              { t: "Fair compliance", d: "We handle DBS, references and Right to Work transparently with you.", k: "02" },
              { t: "Real progression", d: "From day one, your training plan is mapped to CQC standards and your goals.", k: "03" },
            ].map((f) => (
              <div key={f.k} className="cc-card p-8">
                <div className="font-dash text-sm font-bold text-[var(--cc-secondary)] mb-3">{f.k}</div>
                <div className="font-display text-2xl font-bold mb-2">{f.t}</div>
                <p className="text-sm text-[var(--cc-text-soft)] leading-relaxed">{f.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-8">
        <div className="max-w-4xl mx-auto cc-card p-12 text-center" style={{ background: "var(--cc-primary)" }}>
          <h2 className="font-display text-4xl md:text-5xl font-bold text-white mb-4">Ready to make a difference?</h2>
          <p className="text-white/75 text-lg mb-8 max-w-xl mx-auto">Browse current vacancies and apply in minutes.</p>
          <Link to="/jobs" className="inline-flex items-center gap-2 bg-white text-[var(--cc-primary)] font-semibold px-7 py-3.5 rounded-xl hover:bg-[var(--cc-surface)] transition" data-testid="cta-browse-jobs">
            Browse open roles <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <footer className="border-t border-[var(--cc-border)] py-8 px-8 text-sm text-[var(--cc-text-soft)] flex flex-col md:flex-row justify-between gap-4" data-testid="portal-footer">
        <div>
          <div>© Rabah Concept Recruitment · CQC Safer Recruitment compliant</div>
          <div className="mt-1 text-xs">
            <span data-testid="portal-footer-phone">+44 03303211535</span> ·{" "}
            <a href="https://rabahconcept.co.uk" target="_blank" rel="noreferrer">rabahconcept.co.uk</a>
          </div>
        </div>
        <div className="flex gap-5">
          <Link to="/jobs">Jobs</Link>
          <Link to="/track">Track</Link>
          <Link to="/login">Staff</Link>
        </div>
      </footer>
    </div>
  );
}
